import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Avatar,
  Tooltip,
  Box,
  Link,
} from '@material-ui/core';
import Pagination from '@material-ui/lab/Pagination';
import { Alert } from '@material-ui/lab';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { jiraApiRef } from '../../api';
import { Ticket } from '../../types';
import useStyles from './PersonalCardStyles';  // External styles

export const PersonalCard = () => {
  const classes = useStyles();
  const [issues, setIssues] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const issuesPerPage = 5;

  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const jiraApi = useApi(jiraApiRef);

  const fetchIssues = async () => {
    setLoading(true);
    setError(null);
    try {
      const { userEntityRef } = await identityApi.getBackstageIdentity();
      const userEntityName = userEntityRef.split('/')[1];
      const userEntity = await catalogApi.getEntityByRef(`user:default/${userEntityName}`);
      const userEmail = userEntity?.metadata?.email;

      if (!userEmail) {
        throw new Error('User email not found');
      }

      const response = await jiraApi.getIssuesAssignedToUser(userEmail as string);
      setIssues(response.issues);
    } catch (err: any) {
      console.error('Failed to fetch Jira issues:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIssues();
  }, [identityApi, jiraApi]);

  // Pagination logic
  const indexOfLastIssue = currentPage * issuesPerPage;
  const indexOfFirstIssue = indexOfLastIssue - issuesPerPage;
  const currentIssues = issues.slice(indexOfFirstIssue, indexOfLastIssue);

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" mb={2}>
          <Avatar src='/svg/jira.svg' className={classes.jiraIcon}/>
          <Typography variant="h5" gutterBottom>Jira Tickets</Typography>
        </Box>
        {loading ? (
          <CircularProgress />
        ) : error ? (
          <Alert severity="error">{`Error: ${error}`}</Alert>
        ) : (
          <>
              <table className={classes.table}>
                <thead>
                  <tr>
                    <th className={classes.th}>KEY</th>
                    <th className={classes.th}>SUMMARY</th>
                    <th className={classes.th}>PRIORITY</th>
                    <th className={classes.th}>STATUS</th>
                    <th className={classes.th}>ASSIGNEE</th>
                  </tr>
                </thead>
                <tbody>
                  {currentIssues.map((issue) => (
                    <tr key={issue.id}>
                      <td className={classes.td}>
                        <Link
                          href={`https://jira.tools.aws.vodafone.com/browse/${issue.key}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={classes.keyBadge}
                        >
                          {issue.key}
                        </Link>
                      </td>
                      <td className={classes.td}>{issue.fields?.summary}</td>
                      <td className={classes.td}>
                        <Tooltip title={issue.fields?.priority?.name || ''} arrow>
                          <img
                            src={issue.fields?.priority.iconUrl}
                            alt={issue.fields?.priority.name}
                            className={classes.priorityIcon}
                          />
                        </Tooltip>
                      </td>
                      <td className={classes.td}>
                        <span
                          className={`${classes.statusBadge} ${
                            issue.fields?.status.name === 'In Progress'
                              ? classes.statusInProgress
                              : issue.fields?.status.name === 'Draft'
                              ? classes.statusBlocked
                              : classes.statusDraft
                          }`}
                        >
                          {issue.fields?.status.name.toUpperCase()}
                        </span>
                      </td>
                      <td className={classes.td}>
                        {issue.fields?.assignee.displayName ? (
                          <Tooltip title={issue.fields.assignee.displayName} arrow>
                            <Avatar
                              alt={issue.fields.assignee.displayName}
                              src={issue.fields.assignee.avatarUrls['48x48']}
                              className={classes.avatar}
                            />
                        </Tooltip>
                        ) : (
                          <span>Not Assigned</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            <Pagination
              count={Math.ceil(issues.length / issuesPerPage)}
              page={currentPage}
              onChange={handlePageChange}
              className={classes.pagination}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
};
